import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { MapPin, ArrowLeft, ArrowRight, Phone } from "lucide-react";
import { SEO } from "../components/SEO";
import { api } from "../lib/api";
import { BIZ } from "../lib/constants";

export default function AttractionDetail() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => { api.attraction(slug).then(setData).catch(() => setErr(true)); }, [slug]);

    if (err) return <div className="ls-container py-32 text-center text-white">Attraction not found. <Link to="/local/clinton-mo" className="link-underline" style={{ color: "var(--ls-gold)" }}>Back to Local guide</Link></div>;
    if (!data) return <div className="ls-container py-32 text-center" style={{ color: "var(--ls-text-muted)" }}>Loading…</div>;

    const { attraction: a, recommended_products } = data;
    const jsonLd = {
        "@context": "https://schema.org", "@type": "TouristAttraction",
        name: a.name, description: a.short_description, image: a.cover_image_url,
        url: `${BIZ.siteUrl}/local/${a.slug}`,
        ...(a.lat && a.lng ? { geo: { "@type": "GeoCoordinates", latitude: a.lat, longitude: a.lng } } : {}),
    };

    return (
        <>
            <SEO title={a.seo_title || a.name} description={a.seo_description || a.short_description} path={`/local/${a.slug}`} image={a.cover_image_url} jsonLd={jsonLd} />

            {/* Hero */}
            <section className="relative grain" style={{ minHeight: "60vh", marginTop: -72, paddingTop: 72 }}>
                <div className="absolute inset-0" style={{ backgroundImage: `url(${a.cover_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                <div className="absolute inset-0 hero-overlay" />
                <div className="relative ls-container flex items-end" style={{ minHeight: "calc(60vh - 72px)", paddingBottom: 60 }}>
                    <div>
                        <Link to="/local/clinton-mo" className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--ls-text-secondary)" }} data-testid="back-to-local">
                            <ArrowLeft size={14} /> Local guide
                        </Link>
                        <div className="flex items-center gap-2 mt-6 text-xs uppercase tracking-wider" style={{ color: "var(--ls-gold)" }}>
                            <MapPin size={12} /> {a.distance_from_store}
                        </div>
                        <h1 className="font-display text-white mt-3" style={{ fontSize: "clamp(40px, 7vw, 84px)", fontWeight: 500, lineHeight: 1.02, letterSpacing: "-0.02em" }} data-testid="attraction-name">
                            {a.name}
                        </h1>
                        <p className="mt-5 max-w-2xl" style={{ color: "var(--ls-text-secondary)", fontSize: 18, lineHeight: 1.6 }}>
                            {a.short_description}
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-16">
                <div className="ls-container" style={{ maxWidth: 820 }}>
                    <div data-testid="attraction-content" style={{ color: "var(--ls-text-secondary)", fontSize: 17, lineHeight: 1.85 }}>
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                                h2: ({ node, ...p }) => <h2 className="font-display text-white mt-10 mb-4" style={{ fontSize: 28, fontWeight: 500 }} {...p} />,
                                h3: ({ node, ...p }) => <h3 className="font-display text-white mt-7 mb-3" style={{ fontSize: 20, fontWeight: 500 }} {...p} />,
                                p: ({ node, ...p }) => <p className="mb-5" {...p} />,
                                ul: ({ node, ...p }) => <ul className="mb-5 space-y-2 list-disc pl-6" {...p} />,
                                a: ({ node, ...p }) => <a className="link-underline" style={{ color: "var(--ls-gold)" }} {...p} />,
                                strong: ({ node, ...p }) => <strong className="text-white" {...p} />,
                            }}
                        >{a.content}</ReactMarkdown>
                    </div>
                </div>
            </section>

            {recommended_products?.length > 0 && (
                <section className="py-16" style={{ background: "var(--ls-panel)", borderTop: "1px solid var(--ls-border)", borderBottom: "1px solid var(--ls-border)" }}>
                    <div className="ls-container">
                        <span className="eyebrow">Recommended from Liquor Studio</span>
                        <h2 className="font-display text-white mt-4 mb-8" style={{ fontSize: 32, fontWeight: 500 }}>What to bring</h2>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="attraction-recommended">
                            {recommended_products.map(p => (
                                <Link key={p.slug} to={`/spirits/${p.slug}`} className="cat-card block" data-testid={`rec-${p.slug}`}>
                                    <div style={{ aspectRatio: "4/5", overflow: "hidden" }}>
                                        <img src={p.image_url} alt={p.name} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    </div>
                                    <div className="p-4">
                                        <h3 className="font-display text-white" style={{ fontSize: 16, fontWeight: 500 }}>{p.name}</h3>
                                        <p className="text-xs mt-1" style={{ color: "var(--ls-text-muted)" }}>{p.brand}</p>
                                        <p className="font-bebas mt-2" style={{ fontSize: 18, color: "var(--ls-gold)" }}>{p.price_display}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section className="py-20">
                <div className="ls-container text-center">
                    <span className="eyebrow">Plan your visit — start at Liquor Studio</span>
                    <h2 className="font-display text-white mt-4" style={{ fontSize: 32, fontWeight: 500 }}>{BIZ.address.full}</h2>
                    <p className="mt-3" style={{ color: "var(--ls-text-secondary)" }}>Open daily. Phone {BIZ.phone}.</p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/visit" className="btn-primary" data-testid="attraction-visit-cta">Visit Liquor Studio <ArrowRight size={16} /></Link>
                        <a href={`tel:${BIZ.phoneTel}`} className="btn-outline-gold" data-testid="attraction-phone-cta"><Phone size={14} /> Call us</a>
                    </div>
                </div>
            </section>
        </>
    );
}
